import { SalesOccupant, UserPermissions } from '@centecomm/portal-service';
import { useCurrentUser, useSales, useSalesV2 } from '@centecomm/portal-service-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoadStatus } from '../../../Types';
import { SalesActions, SalesActionTypes } from '../actions';
import * as selectors from '../selectors';
import { Occupant, OccupantWithPermissionFlag } from '../Types';
import useLaunchDarkly from 'utils/Hooks/LaunchDarkly/useLaunchDarkly';
import { LaunchDarklyFlags } from 'utils/Hooks/LaunchDarkly/launchDarklyFlags';

type SalesStateHook = (permissions?: UserPermissions[]) => {
    areLoaded: boolean;
    areLoading: boolean;
    hasError: boolean;
    loadState: LoadStatus;
    occupants: Occupant[];
    permissionsCheckedOccupants: Occupant[];
};

const useOccupantsFromSalesState: SalesStateHook = (permissions: UserPermissions[] = []) => {
    const dispatch = useDispatch();
    const { hasOccupantPermission } = useCurrentUser();
    const isSalesV2Enabled = useLaunchDarkly(LaunchDarklyFlags.SalesCompliance);

    const { getSalesOccupants: getSalesOccupantsV2 } = useSalesV2();
    const { getSalesOccupants: getSalesOccupantsV1 } = useSales();

    const preferredSalesApi = isSalesV2Enabled ? getSalesOccupantsV2 : getSalesOccupantsV1;

    const loadState: LoadStatus = useSelector(selectors.salesOccupantsLoadStatus);
    const occupants: Occupant[] = useSelector(selectors.salesOccupantsList);

    React.useEffect(() => {
        if (loadState === LoadStatus.INITIAL_STATE || loadState === LoadStatus.ERROR) {
            dispatch({
                type: SalesActions.LOAD_SALES_OCCUPANTS,
            });

            preferredSalesApi().then((fetchedOccupants: SalesOccupant[]) => {
                dispatch({
                    type: SalesActions.SET_SALES_OCCUPANTS,
                    payload: fetchedOccupants.map(
                        (_) =>
                            ({
                                id: _.occupantId,
                                name: _.occupantName,
                                propertyId: _.propertyId,
                                propertyName: _.propertyName,
                            } as Occupant),
                    ),
                } as SalesActionTypes);
            });
        }
    }, [loadState]);

    // Places flag on occupant object to show if it has the given permissions. The
    // current use case of this is to show a list with active links for those occupants
    // that pass the ViewOccupant permission and static display for those that don't
    const permissionsCheckedOccupants: OccupantWithPermissionFlag[] = React.useMemo(
        () =>
            occupants.map((occupant) => ({
                ...occupant,
                hasPermission: permissions.some((permission) =>
                    hasOccupantPermission(permission, occupant.propertyId, occupant.id),
                ),
            })),
        [occupants, permissions, hasOccupantPermission],
    );

    return {
        areLoaded: loadState === LoadStatus.LOADED,
        areLoading: loadState === LoadStatus.PENDING,
        hasError: loadState === LoadStatus.ERROR,
        loadState,
        occupants,
        permissionsCheckedOccupants,
    };
};

export default useOccupantsFromSalesState;
